import React, { useState } from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetCustomerAddresses } from '../../hooks/customer/useGetCustomerAddresses';
import { useCreateCustomerAddress } from '../../hooks/customer/useCreateCustomerAddress';
import { useUpdateCustomerAddress } from '../../hooks/customer/useUpdateCustomerAddress';
import { useDeleteCustomerAddress } from '../../hooks/customer/useDeleteCustomerAddress';
import AddressForm from '../../components/customer/AddressForm';
import AddressesList from '../../components/customer/AddressesList';
import { useSetDefaultBillingAddress } from '../../hooks/customer/useSetDefaultBillingAddress';
import { useSetDefaultShippingAddress } from '../../hooks/customer/useSetDefaultShippingAddress';
import ModelDelete from '../../components/global/Models/ModelDelete';

const Addresses = () => {
  const { t, i18n } = useTranslation();
  const { data: addresses, isLoading, error, refetch } = useGetCustomerAddresses();
  const createAddress = useCreateCustomerAddress();
  const updateAddress = useUpdateCustomerAddress();
  const deleteAddress = useDeleteCustomerAddress();
  const setDefaultBillingAddressMutation = useSetDefaultBillingAddress();
  const setDefaultShippingAddressMutation = useSetDefaultShippingAddress();

  const [editingAddress, setEditingAddress] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, addressId: null });

  const handleEdit = (address) => {
    setEditingAddress(address);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setEditingAddress(null);
    setShowForm(false);
  };

  const handleSubmit = (addressData) => {
    if (editingAddress) {
      updateAddress.mutate({ id: editingAddress.id, addressData }, {
        onSuccess: () => {
          handleCancel();
          refetch();
        }
      });
    } else {
      createAddress.mutate(addressData, {
        onSuccess: () => {
          handleCancel();
          refetch();
        }
      });
    }
  };

  const handleDeleteClick = (addressId) => {
    setDeleteModal({ open: true, addressId });
  };

  const handleDeleteConfirm = () => {
    deleteAddress.mutate(deleteModal.addressId, {
      onSuccess: () => {
        setDeleteModal({ open: false, addressId: null });
        refetch();
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, addressId: null });
  };

  const handleSetDefaultShipping = (addressId) => {
    setDefaultShippingAddressMutation.mutate(addressId, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  const handleSetDefaultBilling = (addressId) => {
    setDefaultBillingAddressMutation.mutate(addressId, {
      onSuccess: () => {
        refetch();
      }
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6">{t('common.loading', 'Loading...')}</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h6" color="error">
          {t('profile.addressLoadError', 'Failed to load addresses')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('profile.myAddresses', 'My Addresses')}
      </Typography>

      {showForm ? (
        <AddressForm
          address={editingAddress}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={createAddress.isPending || updateAddress.isPending}
        />
      ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              disabled={createAddress.isPending}
            >
              {t('profile.addNewAddress', 'Add New Address')}
            </Button>
          </Box>

          <AddressesList
            addresses={addresses || []}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onSetDefaultShipping={handleSetDefaultShipping}
            onSetDefaultBilling={handleSetDefaultBilling}
            loading={deleteAddress.isPending || setDefaultBillingAddressMutation.isPending || setDefaultShippingAddressMutation.isPending}
          />

          {/* Delete Confirmation Modal */}
          <ModelDelete
            confirmOpen={deleteModal.open}
            handleCloseConfirm={handleDeleteCancel}
            isArabic={i18n.language === 'ar'}
            isDeletingSingle={deleteAddress.isPending}
            handleConfirmDelete={handleDeleteConfirm}
          />
        </Box>
      )}
    </Container>
  );
};

export default Addresses;
